<template>
  <div class="training-page">
    <!-- ============== HERO ============== -->
    <div class="training-hero">
      <div class="training-hero__bg">
        <span class="bubble bubble-1" />
        <span class="bubble bubble-2" />
        <span class="bubble bubble-3" />
      </div>

      <div class="training-hero__content">
        <div class="training-hero__top">
          <div class="training-hero__title-block">
            <div class="training-hero__icon">
              <feather-icon
                icon="AwardIcon"
                size="28"
              />
            </div>
            <div>
              <h2 class="training-hero__title">
                Training Records
              </h2>
              <p class="training-hero__subtitle">
                <feather-icon
                  icon="UserIcon"
                  size="14"
                  class="mr-25"
                />
                {{ attendeeName || 'Engineer' }}
                <span class="dot-sep">•</span>
                Sessions attended &amp; certificates
              </p>
            </div>
          </div>

          <button
            class="hero-btn"
            @click="goBack"
          >
            <feather-icon
              icon="ArrowLeftIcon"
              size="15"
            />
            <span>Back</span>
          </button>
        </div>

        <!-- Stats -->
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-card__icon stat-card__icon--total">
              <feather-icon
                icon="BookOpenIcon"
                size="20"
              />
            </div>
            <div>
              <div class="stat-card__value">
                {{ totalRows }}
              </div>
              <div class="stat-card__label">
                Total Sessions
              </div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-card__icon stat-card__icon--attended">
              <feather-icon
                icon="CheckCircleIcon"
                size="20"
              />
            </div>
            <div>
              <div class="stat-card__value">
                {{ stats.attended }}
              </div>
              <div class="stat-card__label">
                Attended
              </div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-card__icon stat-card__icon--cert">
              <feather-icon
                icon="ShieldIcon"
                size="20"
              />
            </div>
            <div>
              <div class="stat-card__value">
                {{ stats.certificates }}
              </div>
              <div class="stat-card__label">
                Certificates
              </div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-card__icon stat-card__icon--hours">
              <feather-icon
                icon="ClockIcon"
                size="20"
              />
            </div>
            <div>
              <div class="stat-card__value">
                {{ stats.hours }}<small>h</small>
              </div>
              <div class="stat-card__label">
                Training Hours
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============== TOOLBAR ============== -->
    <div class="toolbar">
      <div class="search-box">
        <feather-icon
          icon="SearchIcon"
          size="16"
          class="search-box__icon"
        />
        <input
          v-model="searchTerm"
          type="text"
          class="search-box__input"
          placeholder="Search by training, trainer, venue or code..."
        >
        <button
          v-if="searchTerm"
          class="search-box__clear"
          @click="searchTerm = ''"
        >
          <feather-icon
            icon="XIcon"
            size="14"
          />
        </button>
      </div>

      <div class="chips">
        <button
          v-for="chip in filterChips"
          :key="chip.value"
          class="chip"
          :class="{ 'chip--active': activeFilter === chip.value }"
          @click="activeFilter = chip.value"
        >
          <feather-icon
            :icon="chip.icon"
            size="13"
          />
          <span>{{ chip.label }}</span>
        </button>
      </div>
    </div>

    <!-- ============== LOADING ============== -->
    <div
      v-if="dataLoad"
      class="state-box"
    >
      <b-spinner
        variant="primary"
        label="Loading"
      />
      <p>Loading training records...</p>
    </div>

    <!-- ============== EMPTY ============== -->
    <div
      v-else-if="!filteredRows.length"
      class="state-box"
    >
      <div class="state-box__icon">
        <feather-icon
          icon="InboxIcon"
          size="34"
        />
      </div>
      <h5>No training records found</h5>
      <p>
        {{ rows.length ? 'Try changing your search or filter.' : 'This engineer has no training sessions yet.' }}
      </p>
    </div>

    <!-- ============== LIST ============== -->
    <div
      v-else
      class="training-list"
    >
      <div
        v-for="(item, idx) in filteredRows"
        :key="item.id"
        class="training-card"
        :class="item.is_attended ? 'training-card--attended' : 'training-card--missed'"
        :style="{ animationDelay: `${idx * 40}ms` }"
      >
        <!-- Date block -->
        <div class="date-block">
          <span class="date-block__month">{{ monthOf(item.training_session.from) }}</span>
          <span class="date-block__day">{{ dayOf(item.training_session.from) }}</span>
          <span class="date-block__year">{{ yearOf(item.training_session.from) }}</span>
        </div>

        <!-- Main -->
        <div class="training-card__main">
          <div class="training-card__head">
            <h6
              class="training-card__title"
              :title="item.training && item.training.name"
            >
              {{ (item.training && item.training.name) || '—' }}
            </h6>
            <div class="training-card__badges">
              <span class="pill pill--code">
                <feather-icon
                  icon="HashIcon"
                  size="11"
                />
                {{ item.training_session.code || '—' }}
              </span>
              <span
                class="pill"
                :class="item.training_session.is_internal ? 'pill--internal' : 'pill--external'"
              >
                <feather-icon
                  :icon="item.training_session.is_internal ? 'HomeIcon' : 'GlobeIcon'"
                  size="11"
                />
                {{ item.training_session.is_internal ? 'Internal' : 'External' }}
              </span>
              <span
                class="pill"
                :class="item.is_attended ? 'pill--success' : 'pill--danger'"
              >
                <feather-icon
                  :icon="item.is_attended ? 'CheckCircleIcon' : 'XCircleIcon'"
                  size="11"
                />
                {{ item.is_attended ? 'Attended' : 'Not Attended' }}
              </span>
            </div>
          </div>

          <div class="meta-grid">
            <div class="meta">
              <span class="meta__icon meta__icon--trainer">
                <feather-icon
                  icon="UserCheckIcon"
                  size="14"
                />
              </span>
              <div>
                <div class="meta__label">
                  Trainer
                </div>
                <div class="meta__value">
                  {{ item.training_session.trainer || '—' }}
                </div>
              </div>
            </div>

            <div class="meta">
              <span class="meta__icon meta__icon--venue">
                <feather-icon
                  :icon="isOnline(item.training_session.training_venue) ? 'VideoIcon' : 'MapPinIcon'"
                  size="14"
                />
              </span>
              <div>
                <div class="meta__label">
                  Venue
                </div>
                <div class="meta__value text-capitalize">
                  {{ item.training_session.training_venue || '—' }}
                </div>
              </div>
            </div>

            <div class="meta">
              <span class="meta__icon meta__icon--date">
                <feather-icon
                  icon="CalendarIcon"
                  size="14"
                />
              </span>
              <div>
                <div class="meta__label">
                  Date
                </div>
                <div class="meta__value">
                  {{ dateRange(item.training_session) }}
                </div>
              </div>
            </div>

            <div class="meta">
              <span class="meta__icon meta__icon--time">
                <feather-icon
                  icon="ClockIcon"
                  size="14"
                />
              </span>
              <div>
                <div class="meta__label">
                  Time
                </div>
                <div class="meta__value">
                  {{ timeRange(item.training_session) }}
                  <span
                    v-if="durationOf(item.training_session)"
                    class="duration"
                  >
                    {{ durationOf(item.training_session) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Certificate -->
        <div class="training-card__cert">
          <template v-if="item.certificate_file && item.certificate_file.stream">
            <div class="cert-label">
              <feather-icon
                icon="ShieldIcon"
                size="14"
              />
              Certificate
            </div>
            <div class="cert-actions">
              <button
                type="button"
                class="cert-btn cert-btn--view"
                title="View certificate"
                :disabled="isFileLoading(item, 'view')"
                @click="openCertificate(item, 'view')"
              >
                <b-spinner
                  v-if="isFileLoading(item, 'view')"
                  small
                />
                <feather-icon
                  v-else
                  icon="EyeIcon"
                  size="14"
                />
                <span>View</span>
              </button>
              <button
                type="button"
                class="cert-btn cert-btn--download"
                title="Download certificate"
                :disabled="isFileLoading(item, 'download')"
                @click="openCertificate(item, 'download')"
              >
                <b-spinner
                  v-if="isFileLoading(item, 'download')"
                  small
                />
                <feather-icon
                  v-else
                  icon="DownloadIcon"
                  size="14"
                />
                <span>Download</span>
              </button>
            </div>
          </template>
          <div
            v-else
            class="cert-none"
          >
            <feather-icon
              icon="FileMinusIcon"
              size="18"
            />
            <span>No certificate</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ============== PAGINATION ============== -->
    <div
      v-if="!dataLoad && totalRows > perPage"
      class="pagination-wrapper"
    >
      <div class="pagination-wrapper__info">
        Showing
        <span class="font-weight-bold">{{ (page - 1) * perPage + 1 }}</span>
        -
        <span class="font-weight-bold">{{ Math.min(page * perPage, totalRows) }}</span>
        of
        <span class="font-weight-bold">{{ totalRows }}</span>
      </div>
      <b-pagination
        v-model="page"
        :total-rows="totalRows"
        :per-page="perPage"
        align="end"
        first-number
        last-number
        :total-visible="6"
        class="mb-0 custom-pagination"
        @input="getAllData"
      >
        <template #prev-text>
          <feather-icon
            icon="ChevronLeftIcon"
            size="16"
          />
        </template>
        <template #next-text>
          <feather-icon
            icon="ChevronRightIcon"
            size="16"
          />
        </template>
      </b-pagination>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { BPagination, BSpinner } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// "YYYY-MM-DD" -> Date (local, avoids timezone shifts)
const parseDate = d => {
  if (!d) return null
  const [y, m, day] = String(d).slice(0, 10).split('-').map(Number)
  if (!y || !m || !day) return null
  return new Date(y, m - 1, day)
}

// "HH:mm:ss" -> minutes since midnight
const toMinutes = t => {
  if (!t) return null
  const [h, m] = String(t).split(':').map(Number)
  if (Number.isNaN(h)) return null
  return h * 60 + (m || 0)
}

export default {
  components: { BPagination, BSpinner },
  data() {
    return {
      rows: [],
      totalRows: 0,
      page: 1,
      perPage: 20,
      dataLoad: false,
      loadingFiles: {},
      searchTerm: '',
      activeFilter: 'all',
      filterChips: [
        { value: 'all', label: 'All', icon: 'LayersIcon' },
        { value: 'attended', label: 'Attended', icon: 'CheckCircleIcon' },
        { value: 'missed', label: 'Not Attended', icon: 'XCircleIcon' },
        { value: 'internal', label: 'Internal', icon: 'HomeIcon' },
        { value: 'external', label: 'External', icon: 'GlobeIcon' },
      ],
    }
  },
  computed: {
    attendeeName() {
      return this.rows.length ? this.rows[0].attendee_name : ''
    },
    stats() {
      const attended = this.rows.filter(r => r.is_attended).length
      const certificates = this.rows.filter(r => r.certificate_file && r.certificate_file.stream).length
      const minutes = this.rows
        .filter(r => r.is_attended)
        .reduce((sum, r) => sum + (this.durationMinutes(r.training_session) || 0), 0)
      return {
        attended,
        certificates,
        hours: Math.round((minutes / 60) * 10) / 10,
      }
    },
    filteredRows() {
      const q = this.searchTerm.trim().toLowerCase()
      return this.rows.filter(r => {
        const s = r.training_session || {}
        if (this.activeFilter === 'attended' && !r.is_attended) return false
        if (this.activeFilter === 'missed' && r.is_attended) return false
        if (this.activeFilter === 'internal' && !s.is_internal) return false
        if (this.activeFilter === 'external' && s.is_internal) return false
        if (!q) return true
        return [r.training && r.training.name, s.trainer, s.training_venue, s.code]
          .some(v => v && String(v).toLowerCase().includes(q))
      })
    },
  },
  created() {
    this.getAllData(1)
  },
  methods: {
    async getAllData(page = 1) {
      this.page = page
      this.dataLoad = true
      try {
        const response = await axios.get(
          `engineer/${this.$route.params.id}/training-session-attend`,
          { params: { skip: (page - 1) * this.perPage } },
        )
        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const data = responseData?.data
          this.rows = (data && data[Object.keys(data)[0]]) || []
          this.totalRows = data?.count || data?.length || this.rows.length
        }
      } catch (e) {
        this.rows = []
        this.totalRows = 0
      } finally {
        this.dataLoad = false
      }
    },
    isFileLoading(item, mode) {
      return !!this.loadingFiles[`${item.id}-${mode}`]
    },
    // The file endpoints need the Authorization header, so a plain link gives 401.
    // Fetch the file through axios (token is added by the interceptor) and open it as a blob.
    async openCertificate(item, mode) {
      const file = item.certificate_file
      const url = mode === 'download' ? (file.download || file.stream) : file.stream
      const key = `${item.id}-${mode}`
      if (!url || this.loadingFiles[key]) return

      // Open the tab synchronously so the browser doesn't block it as a popup
      const newTab = mode === 'view' ? window.open('', '_blank') : null

      this.$set(this.loadingFiles, key, true)
      try {
        const response = await axios.get(url, { responseType: 'blob' })
        const blob = new Blob([response.data], { type: response.data.type })
        const blobUrl = window.URL.createObjectURL(blob)

        if (mode === 'view') {
          if (newTab) newTab.location.href = blobUrl
          else window.open(blobUrl, '_blank')
          // Give the new tab time to load before releasing the object URL
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000)
        } else {
          const link = document.createElement('a')
          link.href = blobUrl
          const training = this.cleanFilePart(item.training && item.training.name)
          const attendee = this.cleanFilePart(item.attendee_name)
          link.download = `${[training, attendee].filter(Boolean).join(' - ') || 'certificate'}.pdf`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(blobUrl)
        }
      } catch (error) {
        if (newTab) newTab.close()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Could not load the certificate',
            icon: 'BellIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.$set(this.loadingFiles, key, false)
      }
    },
    cleanFilePart(value) {
      return String(value || '')
        .trim()
        .replace(/[\\/:*?"<>|]/g, '-')
        .replace(/\s+/g, ' ')
        .slice(0, 120)
    },
    goBack() {
      if (window.history.length > 1) this.$router.go(-1)
      else this.$router.push({ name: 'engineers' })
    },
    isOnline(venue) {
      return String(venue || '').toLowerCase().includes('online')
    },
    monthOf(d) {
      const date = parseDate(d)
      return date ? MONTHS[date.getMonth()] : '—'
    },
    dayOf(d) {
      const date = parseDate(d)
      return date ? String(date.getDate()).padStart(2, '0') : '—'
    },
    yearOf(d) {
      const date = parseDate(d)
      return date ? date.getFullYear() : ''
    },
    fmtDate(d) {
      const date = parseDate(d)
      if (!date) return '—'
      return `${String(date.getDate()).padStart(2, '0')} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`
    },
    dateRange(session) {
      if (!session) return '—'
      if (!session.to || session.to === session.from) return this.fmtDate(session.from)
      return `${this.fmtDate(session.from)} → ${this.fmtDate(session.to)}`
    },
    fmtTime(t) {
      const mins = toMinutes(t)
      if (mins === null) return ''
      const h = Math.floor(mins / 60)
      const m = mins % 60
      const suffix = h >= 12 ? 'PM' : 'AM'
      const h12 = h % 12 === 0 ? 12 : h % 12
      return `${h12}:${String(m).padStart(2, '0')} ${suffix}`
    },
    timeRange(session) {
      if (!session || !session.start_time) return '—'
      return `${this.fmtTime(session.start_time)} - ${this.fmtTime(session.end_time)}`
    },
    durationMinutes(session) {
      if (!session) return null
      const start = toMinutes(session.start_time)
      const end = toMinutes(session.end_time)
      if (start === null || end === null || end <= start) return null
      const from = parseDate(session.from)
      const to = parseDate(session.to)
      const days = from && to ? Math.round((to - from) / 86400000) + 1 : 1
      return (end - start) * Math.max(days, 1)
    },
    durationOf(session) {
      const mins = this.durationMinutes(session)
      if (!mins) return ''
      const h = Math.floor(mins / 60)
      const m = mins % 60
      if (h && m) return `${h}h ${m}m`
      return h ? `${h}h` : `${m}m`
    },
  },
}
</script>

<style lang="scss" scoped>
$primary: #297cd6;
$primary-dark: #1c5fa8;
$primary-soft: #e9f2ff;
$teal: #1fa8a0;
$teal-soft: #e3f6f4;
$success: #1ea162;
$success-soft: #e4f7ec;
$danger: #d94a4b;
$danger-soft: #fdeaea;
$amber: #d98a2b;
$amber-soft: #fff3e0;
$violet: #6c63d9;
$violet-soft: #eeedfc;
$dark: #2a2e43;
$muted: #6e6b7b;
$border: #e8edf4;

.training-page {
  padding: 0 4px 32px;
}

.dot-sep {
  margin: 0 8px;
  opacity: 0.7;
}

/* ============ HERO ============ */
.training-hero {
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  padding: 24px 28px 24px;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #297cd6 0%, #3d8fe0 55%, #1fa8a0 130%);
  color: #fff;
  box-shadow: 0 12px 32px -10px rgba(41, 124, 214, 0.45);

  &__bg {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
    .bubble {
      position: absolute;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.09);
    }
    .bubble-1 { width: 220px; height: 220px; top: -80px; right: -60px; }
    .bubble-2 { width: 140px; height: 140px; bottom: -55px; left: 12%; }
    .bubble-3 { width: 90px; height: 90px; top: 35%; left: 55%; }
  }

  &__content { position: relative; z-index: 1; }

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 22px;
    flex-wrap: wrap;
  }

  &__title-block {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__icon {
    width: 56px;
    height: 56px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }

  &__title {
    color: #fff;
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 4px;
  }

  &__subtitle {
    margin: 0;
    font-size: 0.9rem;
    opacity: 0.92;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
}

.hero-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  backdrop-filter: blur(6px);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.28);
    transform: translateX(-2px);
  }
}

/* ============ STATS ============ */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.95);
  color: $dark;
  box-shadow: 0 6px 18px -8px rgba(0, 0, 0, 0.25);

  &__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &--total { background: $primary-soft; color: $primary; }
    &--attended { background: $success-soft; color: $success; }
    &--cert { background: $violet-soft; color: $violet; }
    &--hours { background: $teal-soft; color: $teal; }
  }

  &__value {
    font-size: 1.45rem;
    font-weight: 700;
    line-height: 1.1;
    small { font-size: 0.8rem; margin-left: 2px; color: $muted; }
  }

  &__label {
    font-size: 0.78rem;
    color: $muted;
    margin-top: 2px;
  }
}

/* ============ TOOLBAR ============ */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 18px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid $border;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(41, 124, 214, 0.05);
}

.search-box {
  position: relative;
  flex: 1 1 280px;
  max-width: 420px;

  &__icon {
    position: absolute;
    top: 50%;
    left: 12px;
    transform: translateY(-50%);
    color: $muted;
  }

  &__input {
    width: 100%;
    height: 40px;
    padding: 0 36px 0 38px;
    border: 1px solid $border;
    border-radius: 10px;
    background: #f7f9fc;
    font-size: 0.88rem;
    color: $dark;
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      background: #fff;
      border-color: $primary;
      box-shadow: 0 0 0 3px rgba(41, 124, 214, 0.12);
    }
  }

  &__clear {
    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
    border: 0;
    background: #e8edf4;
    color: $muted;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    &:hover { background: $danger-soft; color: $danger; }
  }
}

.chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 13px;
  border: 1px solid $border;
  border-radius: 999px;
  background: #f7f9fc;
  color: $muted;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover { color: $primary; border-color: rgba(41, 124, 214, 0.4); background: $primary-soft; }

  &--active {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, $primary, #3d8fe0);
    box-shadow: 0 6px 14px -6px rgba(41, 124, 214, 0.6);
    &:hover { color: #fff; background: linear-gradient(135deg, $primary-dark, $primary); }
  }
}

/* ============ STATE BOX ============ */
.state-box {
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border: 1px dashed #d5deea;
  border-radius: 16px;
  color: $muted;

  &__icon {
    width: 72px;
    height: 72px;
    margin: 0 auto 14px;
    border-radius: 50%;
    background: $primary-soft;
    color: $primary;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  h5 { color: $dark; margin-bottom: 6px; }
  p { margin: 8px 0 0; }
}

/* ============ LIST ============ */
.training-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.training-card {
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 18px;
  padding: 16px 18px 16px 22px;
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(41, 124, 214, 0.05);
  overflow: hidden;
  animation: cardIn 0.4s ease both;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
  }
  &--attended::before { background: linear-gradient(180deg, $success, $teal); }
  &--missed::before { background: linear-gradient(180deg, $danger, $amber); }

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(41, 124, 214, 0.35);
    box-shadow: 0 14px 28px -14px rgba(41, 124, 214, 0.35);
  }

  &__main { flex: 1; min-width: 0; }

  &__head {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 14px;
  }

  &__title {
    margin: 0;
    color: $dark;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.4;
  }

  &__badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  &__cert {
    flex: 0 0 190px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 10px;
    padding-left: 18px;
    border-left: 1px dashed #d5deea;
  }
}

@keyframes cardIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Date block */
.date-block {
  flex: 0 0 74px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: linear-gradient(160deg, $primary-soft, #f3f9ff);
  border: 1px solid rgba(41, 124, 214, 0.15);
  color: $primary;
  padding: 8px 0;

  &__month {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  &__day {
    font-size: 1.7rem;
    font-weight: 800;
    line-height: 1.1;
    color: $primary-dark;
  }
  &__year {
    font-size: 0.72rem;
    color: $muted;
  }
}

/* Pills */
.pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;

  &--code { background: #f1f4f9; color: $muted; font-family: monospace; }
  &--internal { background: $primary-soft; color: $primary-dark; }
  &--external { background: $amber-soft; color: $amber; }
  &--success { background: $success-soft; color: $success; }
  &--danger { background: $danger-soft; color: $danger; }
}

/* Meta */
.meta-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.meta {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  &__icon {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &--trainer { background: $violet-soft; color: $violet; }
    &--venue { background: $teal-soft; color: $teal; }
    &--date { background: $primary-soft; color: $primary; }
    &--time { background: $amber-soft; color: $amber; }
  }

  &__label {
    font-size: 0.7rem;
    color: $muted;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  &__value {
    font-size: 0.85rem;
    font-weight: 600;
    color: $dark;
    word-break: break-word;
  }
}

.duration {
  display: inline-block;
  margin-left: 4px;
  padding: 0 6px;
  border-radius: 6px;
  background: $amber-soft;
  color: $amber;
  font-size: 0.7rem;
  font-weight: 700;
}

/* Certificate */
.cert-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: $violet;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.cert-actions {
  display: flex;
  gap: 8px;
}

.cert-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 0.78rem;
  font-weight: 700;
  text-decoration: none !important;
  border: 0;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled { opacity: 0.7; cursor: wait; }

  &--view {
    background: $violet-soft;
    color: $violet;
    &:hover { background: $violet; color: #fff; box-shadow: 0 6px 14px -6px rgba(108, 99, 217, 0.7); }
  }
  &--download {
    background: $teal-soft;
    color: $teal;
    &:hover { background: $teal; color: #fff; box-shadow: 0 6px 14px -6px rgba(31, 168, 160, 0.7); }
  }
}

.cert-none {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px;
  border-radius: 10px;
  background: #f7f9fc;
  color: #a0a8b8;
  font-size: 0.78rem;
}

/* Pagination wrapper */
.pagination-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
  padding: 12px 16px;
  background: #fff;
  border: 1px solid $border;
  border-radius: 14px;

  &__info { color: $muted; font-size: 0.85rem; }
}

/* ============ RESPONSIVE ============ */
@media (max-width: 1200px) {
  .meta-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 992px) {
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .training-card {
    flex-wrap: wrap;
    &__main { flex-basis: calc(100% - 100px); }
    &__cert {
      flex: 1 0 100%;
      padding: 12px 0 0;
      border-left: 0;
      border-top: 1px dashed #d5deea;
    }
  }
}

@media (max-width: 576px) {
  .stats-grid { grid-template-columns: 1fr; }
  .meta-grid { grid-template-columns: 1fr; }
  .training-card__main { flex-basis: 100%; }
  .date-block { flex-direction: row; gap: 8px; flex: 1 0 100%; }
}
</style>

<style lang="scss" src="./Engineers.pagination.scss"></style>
